import { FC } from "react";
import { Select, type SelectProps } from "@rothoblaas/design-system";
import useI18n, { I18n } from "../../hooks/useTranslations";

export const VisibilityScopes = ["public", "internal", "hidden"] as const;

export type VisibilityScopeValue = (typeof VisibilityScopes)[number];

export interface VisibilityScopeOption {
  value: VisibilityScopeValue;
  label: string;
}

type VisibilityScopeLabels = Record<VisibilityScopeValue, string>;

export type VisibilityScopeProps = Omit<
  SelectProps<VisibilityScopeOption, false>,
  "defaultValue" | "getOptionLabel" | "getOptionValue" | "isMulti" | "onChange" | "value"
> & {
  defaultValue?: VisibilityScopeValue;
  onChange?: (value: VisibilityScopeValue) => void;
  value?: VisibilityScopeValue | null;
};

const VisibilityScope: FC<VisibilityScopeProps> = ({
  defaultValue,
  isSearchable = false,
  onChange,
  value,
  ...props
}) => {
  const i18n = useI18n();
  const defaultLabels: VisibilityScopeLabels = getVisibilityScopeLabels(i18n);
  const options: VisibilityScopeOption[] = VisibilityScopes.map((option) => ({
    value: option,
    label: defaultLabels[option],
  }));

  const selectedOption = value === undefined ? undefined : (options.find((option) => option.value === value) ?? null);
  const fallbackOption = options.find((option) => option.value === defaultValue) ?? options[0]!;

  return (
    <Select<VisibilityScopeOption>
      {...props}
      options={options}
      isSearchable={isSearchable}
      getOptionValue={(option) => option.value}
      getOptionLabel={(option) => option.label}
      onChange={(option) => onChange?.(option?.value ?? fallbackOption.value)}
      {...(selectedOption !== undefined ? { value: selectedOption } : { defaultValue: undefined })}
    />
  );
};

export default VisibilityScope;

export const getVisibilityScopeLabels = (i18n: I18n): VisibilityScopeLabels => ({
  internal: i18n.translation.common.rothoblaasUsers,
  public: i18n.translation.common.everyOne,
  hidden: i18n.translation.common.hidden,
});
